import { useEffect, useCallback, useRef } from 'react';
import websocketManager from '../utils/websocketManager';

/**
 * Simplified WebSocket hook using the centralized WebSocket manager
 * This replaces all the individual WebSocket implementations
 */
export const useWebSocketManager = (connectionId, options = {}) => {
  const {
    path = '/api/ws',
    onMessage = null,
    onOpen = null,
    onClose = null,
    onError = null,
    autoConnect = true,
    autoReconnect = true
  } = options;

  const connectionIdRef = useRef(connectionId);
  const optionsRef = useRef(options);

  // Update refs when props change
  useEffect(() => {
    connectionIdRef.current = connectionId;
    optionsRef.current = options;
  }, [connectionId, options]);

  const connect = useCallback(() => {
    if (!connectionIdRef.current) return;
    
    websocketManager.connect(connectionIdRef.current, {
      path,
      onMessage,
      onOpen,
      onClose,
      onError,
      autoReconnect
    });
  }, [path, onMessage, onOpen, onClose, onError, autoReconnect]);

  const disconnect = useCallback(() => {
    if (!connectionIdRef.current) return;
    websocketManager.disconnect(connectionIdRef.current);
  }, []);

  const send = useCallback((data) => {
    if (!connectionIdRef.current) return false;
    return websocketManager.send(connectionIdRef.current, data);
  }, []);

  const isConnected = useCallback(() => {
    if (!connectionIdRef.current) return false;
    return websocketManager.isConnected(connectionIdRef.current);
  }, []);

  const getConnectionState = useCallback(() => {
    if (!connectionIdRef.current) return 'DISCONNECTED';
    return websocketManager.getConnectionState(connectionIdRef.current);
  }, []);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect && connectionIdRef.current) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      if (connectionIdRef.current) {
        disconnect();
      }
    };
  }, [autoConnect, connect, disconnect]);

  return {
    connect,
    disconnect,
    send,
    isConnected,
    getConnectionState
  };
};








